import React, { useState } from 'react';
import './AddTodoModal.css';
import AIPreviewModal from './AIPreviewModal/AIPreviewModal';
import Icon from './Icon';
import LoadingSpinner from './LoadingSpinner';
import RichEditor from './RichEditor/RichEditor';
import { generateTodoDetail } from '../services/aiService';
import { isAxiosError } from '../types/error';
import { HTTP_STATUS, MAX_TODO_DESCRIPTION_LENGTH } from '../constants';
import { useTranslation } from '../i18n';
import Modal from './Modal';

interface AddTodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string, description: string, priority: 'low' | 'medium' | 'high') => Promise<void>;
  isLoading?: boolean;
}

const AddTodoModal: React.FC<AddTodoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const descriptionTooLong = description.trim().length > MAX_TODO_DESCRIPTION_LENGTH;
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  
  // AI Preview Modal states
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiSuggestion, setAISuggestion] = useState<{title: string; description: string} | null>(null);
  const [aiError, setAIError] = useState<string | null>(null);

  // AI suggestion handler
  const handleAISuggestion = async () => {
    if (!description.trim()) {
  setAIError(t('todos.addModal.ai.errors.missingDescription'));
      return;
    }

    setIsAIGenerating(true);
    setAIError(null);
    setIsAIModalOpen(true);
    try {
      // Call AI service (token is handled automatically by authService)
      const aiResult = await generateTodoDetail(description.trim());
      setAISuggestion({
        title: aiResult.title,
        description: aiResult.description
      });
    } catch (error: unknown) {
      // Handle different error types
      let errorMessage = t('todos.addModal.ai.errors.service');
      
      if (isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          errorMessage = t('ai.goalModal.errors.timeout');
        } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
          errorMessage = t('ai.goalModal.errors.networkError');
        } else if (error.response) {
          const status = error.response.status;
          if (status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
            errorMessage = t('ai.goalModal.errors.serviceUnavailable');
          } else if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
            errorMessage = t('ai.goalModal.errors.tooManyRequests');
          } else if (status >= HTTP_STATUS.SERVER_ERROR) {
            errorMessage = t('ai.goalModal.errors.serverError');
          }
        }
      }
      
      setAIError(errorMessage);
      setIsAIModalOpen(false);
    } finally {
      setIsAIGenerating(false);
    }
  };

  // Handle AI suggestion acceptance
  const handleAIAccept = (suggestedTitle: string, suggestedDescription: string) => {
    setTitle(suggestedTitle);
    setDescription(suggestedDescription);
    setAISuggestion(null);
    setAIError(null);
  };

  // Handle AI modal close
  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
    setAISuggestion(null);
    setAIError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || descriptionTooLong) return;

    try {
      await onSubmit(title.trim(), description.trim(), priority);
      // Reset form
      setTitle('');
      setDescription('');
      setPriority('medium');
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleClose = () => {
    if (!isLoading && !isAIGenerating) {
      setTitle('');
      setDescription('');
      setPriority('medium');
      // Reset AI states
      setIsAIModalOpen(false);
      setAISuggestion(null);
      setAIError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        bare
        isOpen={isOpen}
        onClose={handleClose}
        className="modal-backdrop"
        contentClassName="add-todo-modal"
      >
        <div className="modal-header">
          <div>
            <h3>
              <span className="modal-icon"><Icon name="sparkles" /></span>
              {t('todos.addModal.title')}
            </h3>
          </div>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isLoading}
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            <Icon name="close" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="todo-title">{t('todos.addModal.labels.title')}</label>
            <input
              id="todo-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('todos.addModal.placeholders.title')}
              className="form-input"
              required
              disabled={isLoading || isAIGenerating}
              autoFocus
              autoComplete='off'
            />
          </div>

          <div className="form-group">
            <label htmlFor="todo-description">{t('todos.addModal.labels.description')}</label>
            <div className="description-input-group">
              <div className="description-input-wrapper">
                <RichEditor
                  content={description}
                  onChange={setDescription}
                  placeholder={t('todos.addModal.placeholders.description')}
                  disabled={isLoading || isAIGenerating}
                  maxLength={MAX_TODO_DESCRIPTION_LENGTH}
                />
                <div className="ai-button-container">
                  <div className="ai-helper-text">
                    {t('todos.addModal.ai.helper')}
                  </div>
                  <button
                    type="button"
                    className="ai-suggest-button"
                    onClick={handleAISuggestion}
                    disabled={isLoading || isAIGenerating || !description.trim()}
                    title={t('todos.addModal.ai.tooltip')}
                  >
                    {isAIGenerating ? (
                      <>
                        <LoadingSpinner size="small" color="white" hideMessage />
                        <span className="button-text">{t('todos.addModal.ai.buttonLoading')}</span>
                      </>
                    ) : (
                      <>
                        <span className="ai-icon"><Icon name="sparkles" /></span>
                        <span className="button-text">{t('todos.addModal.ai.button')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            
              {aiError && (
                <div className="ai-error-message">
                  {aiError}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t('todos.addModal.labels.priority')}</label>
            <div className="priority-buttons">
              <button
                type="button"
                className={`priority-btn priority-btn--low ${priority === 'low' ? 'priority-btn--active' : ''}`}
                onClick={() => setPriority('low')}
                disabled={isLoading || isAIGenerating}
              >
                <span className="priority-btn__dot">●</span>
                {t('goals.priorities.low')}
              </button>
              <button
                type="button"
                className={`priority-btn priority-btn--medium ${priority === 'medium' ? 'priority-btn--active' : ''}`}
                onClick={() => setPriority('medium')}
                disabled={isLoading || isAIGenerating}
              >
                <span className="priority-btn__dot">●</span>
                {t('goals.priorities.medium')}
              </button>
              <button
                type="button"
                className={`priority-btn priority-btn--high ${priority === 'high' ? 'priority-btn--active' : ''}`}
                onClick={() => setPriority('high')}
                disabled={isLoading || isAIGenerating}
              >
                <span className="priority-btn__dot">●</span>
                {t('goals.priorities.high')}
              </button>
            </div>
          </div>

          <div className="modal-actions">
            <div className="action-buttons-group">
              <button 
                type="button" 
                className="action-button cancel-button"
                onClick={handleClose}
                disabled={isLoading || isAIGenerating}
              >
                <Icon name="close" />
                {t('todos.addModal.actions.cancel')}
              </button>
              <button 
                type="submit" 
                className="action-button submit-button"
                disabled={isLoading || isAIGenerating || !title.trim() || descriptionTooLong}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" color="white" hideMessage />
                    {t('todos.addModal.actions.adding')}
                  </>
                ) : (
                  <>
                    <Icon name="plus" />
                    {t('todos.addModal.actions.add')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* AI Preview Modal */}
      <AIPreviewModal
        isOpen={isAIModalOpen}
        onClose={handleAIModalClose}
        onAccept={handleAIAccept}
        title={aiSuggestion?.title || ''}
        description={aiSuggestion?.description || ''}
        originalMessage={description}
        isLoading={isAIGenerating}
      />
    </>
  );
};

export default AddTodoModal;
